import { Link } from 'react-router-dom';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { Button } from '@/components/ui/button';
import { useEarlyAdopter } from '@/contexts/EarlyAdopterContext';
import { 
  UsersIcon, 
  CalendarIcon, 
  ChatBubbleLeftRightIcon, 
  EyeIcon, 
  BellAlertIcon, 
  CheckCircleIcon, 
  ArrowRightIcon,
  HeartIcon,
  ShieldCheckIcon,
  SparklesIcon,
  HomeIcon,
  ClockIcon,
  UserGroupIcon,
  LifebuoyIcon
} from '@heroicons/react/24/outline';
import { Target } from 'lucide-react';
import familyThrivingHero from '@/assets/family-thriving-hero.png';
import avatarWoman1 from '@/assets/avatar-woman-1.jpg';
import avatarMan1 from '@/assets/avatar-man-1.jpg';
import avatarWoman2 from '@/assets/avatar-woman-2.jpg';
import FamilySystemComparison from '@/components/family/FamilySystemComparison';

const FamilyMembers = () => {
  const { openModal } = useEarlyAdopter();
  const howItHelpsFeatures = [
    {
      title: "Shared Daily Signals",
      description: "See simple indicators that routines are happening and life at home is steady—without needing to interrupt or check in constantly.",
      icon: EyeIcon,
      accentColor: "primary"
    },
    {
      title: "Wellness & Medication Awareness",
      description: "Stay informed about patterns and changes over time, not every detail of every day.",
      icon: HeartIcon,
      accentColor: "emerald"
    },
    {
      title: "Coordinated Alerts",
      description: "When something shifts, the right people are notified at the right time—no group texts, no confusion.",
      icon: BellAlertIcon,
      accentColor: "amber"
    },
    {
      title: "Support Without Pressure",
      description: "ThriveFamily keeps families connected and helpful without turning everyday life into a project.",
      icon: ShieldCheckIcon,
      accentColor: "primary"
    }
  ];


  return (
    <>
      <EnhancedSEO 
        title="ThriveFamily – Family Coordination for Home Safety"
        description="ThriveFamily gives you a clear, shared view of how things are going—so you can stay connected, informed, and confident as your loved one lives fully at home."
        canonical="https://seniorthrive.com/thrivefamily"
      />

      {/* HERO SECTION - Full-width image like homepage */}
      <section className="relative w-full min-h-[75vh] lg:min-h-[85vh] overflow-hidden">
        {/* Full-width background image - positioned to show the older woman */}
        <div 
          className="absolute inset-0 bg-cover bg-[75%_center]"
          style={{ backgroundImage: `url(${familyThrivingHero})` }}
        />
        
        {/* Subtle gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-transparent" />
        
        {/* Content positioned on the left side with readable backdrop */}
        <div className="relative h-full min-h-[75vh] lg:min-h-[85vh] flex items-center">
          <div className="container mx-auto px-4 md:px-8 lg:px-12">
            <div className="max-w-2xl pt-24 md:pt-32 pb-20 md:pb-28">
              {/* Semi-transparent backdrop for text readability */}
              <div className="bg-black/50 backdrop-blur-sm rounded-2xl p-8 md:p-10 text-left">
                {/* Badge */}
                <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 w-fit mb-6">
                  <SparklesIcon className="w-4 h-4 text-white" />
                  <span className="text-sm font-semibold text-white uppercase tracking-wider">ThriveFamily</span>
                </div>
                
                {/* Main headline */}
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight leading-[1.1] drop-shadow-lg">
                  The Family Operating System for{' '}
                  <span className="text-primary-foreground">Thriving at Home</span>
                </h1>
                
                {/* Subheadline */}
                <p className="text-lg sm:text-xl lg:text-2xl text-white/90 mb-6 leading-relaxed drop-shadow-md">
                  ThriveFamily gives you a clear, shared view of how things are going—so you can stay connected, informed, and confident as your loved one thrives at home.
                </p>
                
                {/* Trust line */}
                <div className="flex items-center gap-3 text-white/80 mb-6">
                  <ShieldCheckIcon className="w-5 h-5" />
                  <span className="font-medium">Built to reduce worry, not add responsibility.</span>
                </div>

                {/* CTA */}
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Button size="lg" className="text-lg px-8 py-6 rounded-xl font-semibold bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl" onClick={() => openModal('Family Members Hero - Request Early Access')}>
                      Request Early Access
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Button>
                </div>

                {/* Social proof */}
                <div className="flex items-center gap-4 text-sm text-white/70">
                  <div className="flex -space-x-2">
                    <img src={avatarWoman1} alt="ThriveFamily user" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                    <img src={avatarMan1} alt="ThriveFamily user" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                    <img src={avatarWoman2} alt="ThriveFamily user" className="w-8 h-8 rounded-full border-2 border-white/30 object-cover" />
                  </div>
                  <span>Trusted by families who want clarity, connection, and peace of mind.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: WHY FAMILIES NEED A SYSTEM */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-secondary/20 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
                Why Families Need a System
              </h2>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
                When support is spread across texts, calls, and memory, everyone ends up guessing. That guessing creates stress, miscommunication, and unnecessary worry.
              </p>
            </div>
            
            <div className="bg-gradient-to-br from-primary/5 to-accent/10 rounded-2xl p-8 lg:p-10 border border-primary/15 shadow-md mb-10">
              <p className="text-xl lg:text-2xl text-foreground font-medium text-center leading-relaxed">
                A system replaces that chaos with steady signals—so you know when things are going well and when your help actually matters.
              </p>
            </div>
            
            {/* Supporting Points */}
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { icon: ChatBubbleLeftRightIcon, text: "End the \"just checking in\" phone call", accent: "primary" },
                { icon: ArrowRightIcon, text: "Shift from reacting to problems to supporting daily momentum", accent: "emerald" },
                { icon: UsersIcon, text: "Keep siblings, spouses, and extended family aligned without friction", accent: "amber" }
              ].map((point, i) => {
                const colors = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/10 hover:border-primary/25' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 hover:border-emerald-300' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 hover:border-amber-300' }
                };
                const style = colors[point.accent as keyof typeof colors];
                return (
                  <div key={i} className={`flex items-start gap-4 p-5 rounded-xl bg-card border ${style.border} hover:shadow-lg transition-all duration-300`}>
                    <div className={`w-10 h-10 ${style.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                      <point.icon className={`w-5 h-5 ${style.icon}`} />
                    </div>
                    <p className="text-foreground font-medium leading-relaxed">{point.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: WHAT CHANGES - COMPARISON */}
      <FamilySystemComparison />

      {/* SECTION 4: HOW THRIVEFAMILY HELPS */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-accent/10 via-background to-secondary/15">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                How ThriveFamily Helps
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Practical, not heavy. Connected, not controlling.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              {howItHelpsFeatures.map((feature, idx) => {
                const accentStyles = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/15 hover:border-primary/30', shadow: 'hover:shadow-primary/5' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/50 hover:border-emerald-400/50', shadow: 'hover:shadow-emerald-500/5' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/50 hover:border-amber-400/50', shadow: 'hover:shadow-amber-500/5' }
                };
                const style = accentStyles[feature.accentColor as keyof typeof accentStyles] || accentStyles.primary;
                
                return (
                  <div key={idx} className={`group p-8 rounded-2xl bg-card border ${style.border} transition-all duration-300 hover:shadow-xl ${style.shadow}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <div className={`w-12 h-12 ${style.bg} rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform`}>
                        <feature.icon className={`w-6 h-6 ${style.icon}`} />
                      </div>
                      <h3 className="text-xl font-bold text-foreground">{feature.title}</h3>
                    </div>
                    <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: DESIGNED TO STRENGTHEN RELATIONSHIPS */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-primary/5 via-secondary/10 to-accent/15">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="w-20 h-20 bg-gradient-to-br from-primary/15 to-accent/20 rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-sm">
              <UserGroupIcon className="w-10 h-10 text-primary" />
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-6">
              Designed to Strengthen Relationships
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Supporting someone you love should bring you closer, not create tension. ThriveFamily helps families stay aligned and supportive while preserving independence and trust.
            </p>
            <div className="inline-block bg-card/80 backdrop-blur-sm rounded-2xl px-10 py-6 border border-primary/15 shadow-lg">
              <p className="text-xl lg:text-2xl text-foreground font-semibold">
                Everyone stays connected. No one feels managed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 6: EVERYTHING YOUR FAMILY GETS */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-background via-muted/20 to-secondary/15">
        <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">Everything Your Family Gets</h2>
              <p className="text-xl text-muted-foreground">Designed to support families without overwhelm</p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { 
                  icon: UsersIcon, 
                  title: "Up to 5 Family Members", 
                  description: "Everyone who needs context can stay aligned without chaos or crossed wires.",
                  accent: "primary"
                },
                { 
                  icon: Target, 
                  title: "ThriveScore at a Glance", 
                  description: "A simple signal that shows when things are steady and when something may need attention.",
                  accent: "amber"
                },
                { 
                  icon: CheckCircleIcon, 
                  title: "Daily Check-Ins Without Calling", 
                  description: "See \"I'm okay\" moments without awkward phone tag or constant interruptions.",
                  accent: "emerald"
                },
                { 
                  icon: HomeIcon, 
                  title: "Room Safety & Home Awareness", 
                  description: "Shared insight into home safety so everyone understands risks and improvements without guessing.",
                  accent: "primary"
                },
                { 
                  icon: CalendarIcon, 
                  title: "Shared Family Calendar", 
                  description: "Appointments, plans, and important moments visible to the people who need to know.",
                  accent: "amber"
                },
                { 
                  icon: ChatBubbleLeftRightIcon, 
                  title: "Family Communication Hub", 
                  description: "One place for updates, notes, and conversations so nothing important gets lost in texts.",
                  accent: "emerald"
                },
                { 
                  icon: EyeIcon, 
                  title: "Wellness & Routine Visibility", 
                  description: "Understand patterns over time, not every detail of every day.",
                  accent: "primary"
                },
                { 
                  icon: UserGroupIcon, 
                  title: "Guidance for Family Conversations", 
                  description: "Helpful prompts and call scripts for difficult or sensitive conversations so you are not guessing what to say.",
                  accent: "amber"
                },
                { 
                  icon: LifebuoyIcon, 
                  title: "On-Demand Answers & Support", 
                  description: "Clear, helpful answers to common questions about health, routines, activities, and next steps when you need them.",
                  accent: "emerald"
                }
              ].map((feature, i) => {
                const colors = {
                  primary: { bg: 'bg-primary/10', icon: 'text-primary', border: 'border-primary/10 hover:border-primary/25' },
                  emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400', border: 'border-emerald-200/40 hover:border-emerald-300' },
                  amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400', border: 'border-amber-200/40 hover:border-amber-300' }
                };
                const style = colors[feature.accent as keyof typeof colors];
                return (
                  <div key={i} className={`bg-card p-6 rounded-xl border ${style.border} hover:shadow-lg transition-all duration-300`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-10 h-10 ${style.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <feature.icon className={`w-5 h-5 ${style.icon}`} />
                      </div>
                      <h3 className="font-semibold text-foreground">{feature.title}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                );
              })}
            </div>
            
            {/* ThriveFamily+ footer note */}
            <div className="mt-12 text-center">
              <div className="inline-block bg-card/80 backdrop-blur-sm rounded-xl px-6 py-4 border border-primary/10">
                <p className="text-muted-foreground">
                  Need clinical tracking, medication management, or caregiver coordination?{' '}
                  <Link to="/family-caregiver-tools" className="text-primary font-semibold hover:underline">ThriveFamily+</Link> adds the tools you need.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 lg:py-28">
        <div className="container mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl max-w-5xl mx-auto">
            <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-glow to-primary/80"></div>
            <div className="absolute inset-0 opacity-30">
              <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-radial from-primary-foreground/20 to-transparent rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-1/4 h-1/4 bg-gradient-radial from-primary-foreground/15 to-transparent rounded-full blur-2xl"></div>
            </div>
            
            <div className="relative text-center p-12 lg:p-16 text-primary-foreground">
              <h2 className="text-3xl lg:text-4xl xl:text-5xl font-bold mb-6">
                Stay Connected. Stay Informed.<br />
                <span className="opacity-90">Support Thriving at Home.</span>
              </h2>
              <p className="text-xl mb-8 opacity-95 max-w-2xl mx-auto leading-relaxed">
                ThriveFamily helps you support the life your loved one is actively living—with clarity, calm, and the right tools when they matter.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  variant="secondary"
                  className="text-lg px-10 py-6 bg-white text-primary hover:bg-white/95 shadow-lg hover:shadow-xl transition-all group font-semibold"
                  onClick={() => openModal('Family Members CTA - Request Early Access')}
                >
                    Request Early Access
                    <ArrowRightIcon className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
              
              <p className="text-sm opacity-80 mt-6">
                Trusted by families who want peace of mind without hovering.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FamilyMembers;
